import React, { useEffect, useRef, useState } from 'react'
import { cls } from '@utils/classnames'

const INPUT_ID = 'location-search-input'
const ERROR_ID = 'location-search-error'

const LocationSearchInputField: React.FC<{
  label?: string
  placeholder: string
  value: string
  onChange: (newValue: string) => void
  hasError: boolean
  onSearch: () => void
  onGpsRequest?: () => void
  predictions: google.maps.places.AutocompletePrediction[] | null
  autocompleteIsPending?: boolean
  searchCommittedCount?: number
  ariaLabelGpsButton?: string
  ariaLabelSearchButton?: string
  ariaLabelSearchError?: string
}> = ({
  label,
  placeholder,
  value,
  hasError,
  predictions,
  autocompleteIsPending,
  searchCommittedCount,
  onChange,
  onSearch,
  onGpsRequest,
  ariaLabelGpsButton,
  ariaLabelSearchButton,
  ariaLabelSearchError,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const listReference = useRef<HTMLUListElement | null>(null)

  useEffect(() => {
    if (searchCommittedCount) {
      setIsOpen(false)
    }
  }, [searchCommittedCount])

  function handleContainerBlur(event: React.FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsOpen(false)
    }
  }

  function handleSearchKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      setIsOpen(false)
      onSearch()
    } else if (event.key === 'ArrowDown' && isOpen) {
      event.preventDefault()
      listReference.current?.querySelector<HTMLElement>('li')?.focus()
    }
  }

  return (
    <div
      className={cls({
        'cmp-location-search-input-field': true,
        'cmp-location-search-input-field--has-error': hasError,
      })}
      onBlur={handleContainerBlur}
    >
      {label ? (
        <label className="cmp-location-search-input-field__label" htmlFor={INPUT_ID}>
          {label}
        </label>
      ) : null}
      <input
        id={INPUT_ID}
        className="cmp-location-search-input-field__input"
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          setIsOpen(true)
          onChange(event.target.value)
        }}
        onKeyDown={handleSearchKeyDown}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? ERROR_ID : undefined}
        aria-label={!label ? placeholder : undefined}
      />
      {hasError && (
        <span key={searchCommittedCount} id={ERROR_ID} className="cmp-location-search-input-field__error" role="alert">
          {ariaLabelSearchError || 'Please enter a valid location'}
        </span>
      )}
      {isOpen && predictions && predictions.length > 0 && (
        <ul ref={listReference} className="cmp-location-search-input-field__autocomplete-datalist" role="listbox">
          {predictions.map((prediction) => (
            <li
              key={prediction.place_id}
              className="cmp-location-search-input-field__autocomplete-option"
              role="option"
              aria-selected={false}
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(prediction.description)
                setIsOpen(false)
              }}
              onKeyDown={(event) => {
                switch (event.key) {
                  case 'Enter':
                  case ' ': {
                    event.preventDefault()
                    onChange(prediction.description)
                    setIsOpen(false)
                    break
                  }
                  case 'ArrowDown': {
                    event.preventDefault()
                    ;(event.currentTarget.nextElementSibling as HTMLElement | null)?.focus()
                    break
                  }
                  case 'ArrowUp': {
                    event.preventDefault()
                    const previous = event.currentTarget.previousElementSibling as HTMLElement | null
                    if (previous) {
                      previous.focus()
                    } else {
                      document.querySelector<HTMLElement>(`#${INPUT_ID}`)?.focus()
                    }
                    break
                  }
                  case 'Escape': {
                    setIsOpen(false)
                    document.querySelector<HTMLElement>(`#${INPUT_ID}`)?.focus()
                    break
                  }
                  default:
                    break
                }
              }}
            >
              {prediction.description}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="cmp-location-search-input-field__icon ri-focus-3-line"
        aria-label={ariaLabelGpsButton || 'Use my location'}
        onClick={onGpsRequest}
      />
      <button
        type="button"
        className={cls({
          'cmp-location-search-input-field__icon': true,
          'ri-arrow-right-line': !autocompleteIsPending,
          'ri-refresh-line': autocompleteIsPending,
        })}
        aria-label={ariaLabelSearchButton || 'Search'}
        onClick={onSearch}
      />
    </div>
  )
}

export default LocationSearchInputField
